import {
  Component,
  OnInit,
  Input,
  ViewChild,
  ElementRef,
  OnDestroy,
  NgZone,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import TrimbleMaps, { LngLatLike } from '@trimblemaps/trimblemaps-js';
import { debounce } from 'lodash-es';
import { AuthenticationService } from 'src/app/auth/services/authentication.service';
import { MapService } from 'src/app/services/map.service';
import { UserService } from 'src/app/services/user.service';
import {
  getRegionSpecificMapInfo,
  Locate,
} from 'src/app/_helpers/region-helper';
import { SubscriptionManager } from 'src/app/_helpers/subscription-manager';
import { mapLocationOption } from 'src/app/_models/mapLocationOption';

@Component({
  selector: 'app-trimble-map',
  templateUrl: './map.component.html',
})
export class MapComponent implements OnInit, OnDestroy {
  @Input() mapStyle = 'TRANSPORTATION';
  @ViewChild('map', { static: true }) mapElement: ElementRef;

  map: TrimbleMaps.Map;

  private resizeObserver: ResizeObserver;
  private sm = new SubscriptionManager();
  constructor(
    private mapService: MapService,
    private activateRoute: ActivatedRoute,
    private userService: UserService,
    private router: Router,
    private ngZone: NgZone,
    private authService: AuthenticationService
  ) {
    // Debouncing to avoid callback spamming during animations
    this.resizeObserver = new ResizeObserver(
      debounce(() => {
        this.mapService.resize();
      }, 100)
    );
  }

  ngOnInit(): void {
    // Listen for changes in the size of the map div
    this.resizeObserver.observe(this.mapElement.nativeElement);
    const regionInfo: Locate = getRegionSpecificMapInfo(
      this.userService.getUser()?.region
    );

    const queryParams = this.activateRoute.snapshot.queryParams;
    const mapInfo = this.getMapLocation(queryParams, regionInfo);
    this.map = this.mapService.initMap({
      container: this.mapElement.nativeElement,
      style: TrimbleMaps.Common.Style[this.mapStyle],
      center: [mapInfo.center[0], mapInfo.center[1]],
      zoom: mapInfo.zoom,
      hash: false,
      region: regionInfo.region,
      preserveDrawingBuffer: true,
    });
    this.sm.manage = this.mapService.mapLocation$.subscribe((loc) => {
      if (loc.zoom) {
        const centerParams = this.getCenterParam(loc);
        this.ngZone.run(() => {
          this.navigateWithCenterParams(centerParams);
        });
      }
    });
    this.sm.manage = this.authService.currentUser.subscribe((user) => {
      if (!user) {
        this.mapService.clearMaplocation();
      }
    });
  }

  getMapLocation(params, regionInfo: Locate): mapLocationOption {
    if (params.center && parseInt(params.center.slice(0, 1)) !== 0) {
      return this.getMapLocationOptionFromCenterParam(params.center);
    }
    return this.mapService.getLatestMapLocation() || regionInfo;
  }
  getCenterParam(mapLocation: mapLocationOption): string {
    return [
      mapLocation.zoom > 0 ? Math.floor(mapLocation?.zoom) : 0,
      mapLocation.center[1],
      mapLocation.center[0],
    ].join(',');
  }
  getMapLocationOptionFromCenterParam(centerParam): mapLocationOption {
    const mapLoc = centerParam.split(',').map((info) => parseFloat(info));
    const [zoom, lat, lng] = mapLoc;
    const center: LngLatLike = [lng, lat];
    return { zoom, center };
  }
  navigateWithCenterParams(param: string) {
    this.router.navigate([''], {
      relativeTo: this.activateRoute,
      queryParams: { center: param },
    });
  }

  ngOnDestroy(): void {
    this.mapService.destroyMap();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    this.sm.unsubscribe();
  }
}
